/**
 * Shared context + system prompt building for chat send, thread summary, and archive.
 * Keeps parity with a normal message: explicit/RAG context from orchestrators, then
 * thinking + GitHub extensions. Super prompt and user personalization (incl. resume)
 * live in contextOrchestrator.buildSystemPrompt — do not duplicate here.
 */
import { contextOrchestrator, type ContextPayload } from "@/services/contextOrchestrator";
import { contextOrchestratorRAG, type EnhancedContextPayload } from "@/services/contextOrchestratorRAG";
import { getThoughtProcessPreferences } from "@/lib/thoughtProcessPreferences";
import { getChunkType, getRepoTreePathsFromIndex } from "@/services/repoRagService";
import {
  extractRepoTreePaths,
  type RepoContextMode,
} from "@/services/githubRepoContextRouter";
import { buildRepoContextWithAgenticRetrieval } from "@/services/githubRepoAgenticRetrieval";
import {
  buildEditPlan,
  formatEditPlanBlock,
  isEditPlanAllowed,
} from "@/services/repoEditPlanner";
import * as githubService from "@/services/githubService";
import { QUICK_PROMPTS } from "@/data/aiToolkit";

export function getExternalSuperPromptSystemBlock(
  selectedSuperPrompt: { id: string; promptTemplate: string } | null
): string | null {
  if (!selectedSuperPrompt?.promptTemplate?.trim()) return null;
  const inRegistry = QUICK_PROMPTS.some((p) => p.id === selectedSuperPrompt.id);
  if (inRegistry) return null;
  return `You are operating under the following prompt template:\n\n${selectedSuperPrompt.promptTemplate.trim()}\n\nFollow this template and guidance when responding to the user's messages.`;
}

export interface BuildContextAwareSystemPromptParams {
  threadId: string | null;
  lastUserMessage: string | undefined;
  selectedSpaceIds: string[];
  selectedTopicId: string | null;
  selectedDocumentIds: string[];
  usePersonalization: boolean;
  hasPersonalizationData: boolean;
  sourceOfTruthEnabled: boolean;
  agentId: string | null;
  selectedSuperPrompt: { id: string; promptTemplate?: string } | null;
  selectedWorkflow: { id: string } | null;
  useRAG: boolean;
  /** When true and a space or topic is selected, pack broad coverage instead of top-k only. */
  useFullScope?: boolean;
  /** Limit retrieved chunks to these shelf ids. Omit to keep today's retrieval. */
  shelfIds?: string[] | null;
}

export async function buildContextAwareSystemPromptCore(params: BuildContextAwareSystemPromptParams): Promise<{
  systemPrompt: string;
  contextPayload: ContextPayload | EnhancedContextPayload | null;
  phaseMarks: {
    usedRagPath: boolean;
    contextFetchDone: number;
    promptBuildDone: number;
  };
}> {
  try {
    const startedAt = performance.now();
    const spaceId = params.selectedSpaceIds[0] || null;
    const topicId = params.selectedTopicId || null;
    const baseOptions = {
      personalizationEnabled: params.usePersonalization && params.hasPersonalizationData,
      sourceOfTruthEnabled: params.sourceOfTruthEnabled,
      agentId: params.agentId,
      superPromptId: params.selectedSuperPrompt?.id || null,
      superPromptVariables: {} as Record<string, string>,
      workflowId: params.selectedWorkflow?.id || null,
      workflowNodeId: null as string | null,
      workflowVariables: {} as Record<string, unknown>,
      selectedDocumentIds: params.selectedDocumentIds,
    };
    const thoughtPrefs = getThoughtProcessPreferences();
    const ragOptions = {
      limit: thoughtPrefs.ragLimit,
      similarityThreshold: thoughtPrefs.ragSimilarityThreshold,
      hybridMode: thoughtPrefs.ragHybridMode,
      hybridSearch: thoughtPrefs.ragHybridSearch,
      useRerank: thoughtPrefs.ragUseRerank,
      scopeBreadth: params.useFullScope ? 'entire' : 'retrieval',
      ...(params.shelfIds ? { shelfIds: params.shelfIds } : {}),
    };

    let contextPayload: ContextPayload | EnhancedContextPayload | null = null;
    let usedRagPath = false;

    if (params.useRAG && params.lastUserMessage && String(params.lastUserMessage).trim().length > 0) {
      usedRagPath = true;
      if (params.threadId) {
        contextPayload = await contextOrchestratorRAG.getEnhancedContext(params.threadId, {
          ...baseOptions,
          spaceId,
          topicId,
          useRAG: true,
          lastUserMessage: String(params.lastUserMessage).trim(),
          ragOptions,
        });
      } else {
        contextPayload = await contextOrchestratorRAG.getEnhancedContextForNewThread({
          spaceId,
          topicId,
          personalizationEnabled: params.usePersonalization && params.hasPersonalizationData,
          sourceOfTruthEnabled: params.sourceOfTruthEnabled,
          agentId: params.agentId,
          superPromptId: params.selectedSuperPrompt?.id || null,
          superPromptVariables: {},
          selectedDocumentIds: params.selectedDocumentIds,
          useRAG: true,
          lastUserMessage: String(params.lastUserMessage).trim(),
          ragOptions,
        });
      }
    }

    if (!contextPayload) {
      if (params.threadId) {
        contextPayload = await contextOrchestrator.getContextForThread(params.threadId, {
          ...baseOptions,
          spaceId,
          topicId,
        });
      } else {
        const newCtx = await contextOrchestrator.getContextForNewThread({
          spaceId,
          topicId,
          personalizationEnabled: params.usePersonalization && params.hasPersonalizationData,
          sourceOfTruthEnabled: params.sourceOfTruthEnabled,
          agentId: params.agentId,
          superPromptId: params.selectedSuperPrompt?.id || null,
          superPromptVariables: {},
          selectedDocumentIds: params.selectedDocumentIds,
        });
        contextPayload = { ...newCtx, threadId: "" } as ContextPayload;
      }
    }
    const contextFetchDone = Math.round(performance.now() - startedAt);

    let systemPrompt: string;
    if (contextPayload) {
      const ragList =
        "ragSources" in contextPayload ? (contextPayload as EnhancedContextPayload).ragSources : undefined;
      if (Array.isArray(ragList) && ragList.length > 0) {
        const basePayloadForPrompt: ContextPayload = {
          ...contextPayload,
          allSources: [],
          citationMap: new Map(),
          metadata: { ...contextPayload.metadata, totalSources: 0 },
        };
        const baseSystemPrompt = await contextOrchestrator.buildSystemPromptAsync(basePayloadForPrompt);
        systemPrompt = contextOrchestratorRAG.generateRAGSystemPrompt(
          contextPayload as EnhancedContextPayload,
          baseSystemPrompt || "You are a helpful AI assistant."
        );
      } else {
        systemPrompt = await contextOrchestrator.buildSystemPromptAsync(contextPayload as ContextPayload);
      }
    } else {
      systemPrompt = "";
    }
    const promptBuildDone = Math.round(performance.now() - startedAt);

    return {
      systemPrompt,
      contextPayload,
      phaseMarks: { usedRagPath, contextFetchDone, promptBuildDone },
    };
  } catch (e) {
    console.error("Error building context-aware prompt:", e);
    return {
      systemPrompt: "",
      contextPayload: null,
      phaseMarks: { usedRagPath: false, contextFetchDone: 0, promptBuildDone: 0 },
    };
  }
}

export function buildSyntheticRagQueryForSummary(opts: {
  messages: Array<{ role: string; content: unknown }>;
  threadTitle?: string | null;
  maxChars?: number;
}): string {
  const maxChars = opts.maxChars ?? 1200;
  const userTexts: string[] = [];
  for (let i = opts.messages.length - 1; i >= 0 && userTexts.length < 3; i--) {
    const m = opts.messages[i];
    if (m.role !== "user") continue;
    const t = typeof m.content === "string" ? m.content : String(m.content ?? "");
    if (t.trim()) userTexts.unshift(t.trim());
  }
  let q = userTexts.join("\n---\n");
  if (!q.trim()) {
    q = [opts.threadTitle?.trim() || "Untitled chat", "Summarize this conversation for archival purposes."].join("\n");
  }
  if (q.length > maxChars) q = q.slice(0, maxChars);
  return q;
}

export function getPreferredModelFromContextPayload(
  payload: ContextPayload | EnhancedContextPayload | null
): string | null {
  if (!payload?.agent) return null;
  const pm = payload.agent.preferredModel;
  return typeof pm === "string" && pm.trim() ? pm.trim() : null;
}

export interface GitHubChatSystemExtensionResult {
  prompt: string;
  allowRepoEdit: boolean;
  contextMode: RepoContextMode;
  resolvedPaths: string[];
  fullFilePaths: string[];
  repoTreePaths: string[];
  retrievalRounds?: number;
  sufficiencyReason?: string;
}

function fileExtensionForPath(filePath: string): string {
  const dot = filePath.lastIndexOf(".");
  return dot >= 0 ? filePath.slice(dot + 1).toLowerCase() : "text";
}

/** Tree paths: React cache → [REPO_TREE] index chunk → GitHub (last resort). */
async function resolveRepoTreePaths(
  owner: string,
  repo: string,
  cachedRepoContext: unknown
): Promise<string[]> {
  const fromCache = extractRepoTreePaths(cachedRepoContext);
  if (fromCache.length > 0) return fromCache;

  const fromIndex = await getRepoTreePathsFromIndex(owner, repo);
  if (fromIndex.length > 0) return fromIndex;

  try {
    const tree = await githubService.getRepoTree(owner, repo);
    return tree.tree.filter((i) => i.type === "blob").map((i) => i.path);
  } catch (e) {
    console.warn("[Chat] Failed to fetch repo tree fallback:", e);
    return [];
  }
}

function buildEditInstructionsBlock(opts: {
  allowRepoEdit: boolean;
  contextMode: RepoContextMode;
  hasFullFiles: boolean;
  multiRepo?: boolean;
}): string {
  if (opts.contextMode === "clarification_required") {
    return `
=== CODE EDIT INSTRUCTIONS ===
The user appears to want a code change, but the target file could not be resolved or fetched from GitHub.

Do NOT emit %%REPO_EDIT%%.
Ask the user for the exact file path (as it appears in the repository tree), or suggest refreshing the repo index from the GitHub integration panel.
You may still explain related code from the excerpts above.`;
  }

  if (opts.contextMode === "full_file_required" && !opts.allowRepoEdit) {
    return `
=== CODE EDIT INSTRUCTIONS ===
Edit intent was detected but the full target file could not be fetched from GitHub.

Do NOT emit %%REPO_EDIT%%.
Ask the user for the exact file path or suggest refreshing the repo index.
`;
  }

  const safetyRule = opts.hasFullFiles
    ? "\n6. Only emit %%REPO_EDIT%% for a file if its COMPLETE current contents appear under FULL FILE CONTEXT above."
    : "";

  const clarifyFirstRule = opts.allowRepoEdit
    ? "\n7. You MAY ask 1–2 clarifying questions if requirements are ambiguous, but once clear you SHOULD emit %%REPO_EDIT%% when proposing code changes — do not refuse solely because similar functionality already exists."
    : "";

  if (opts.multiRepo) {
    return `
=== CODE EDIT INSTRUCTIONS ===
Multiple GitHub repositories are attached. If your response INCLUDES CODE CHANGES, append this block at the very END of your response.

%%REPO_EDIT%%
[
  {
    "repo": "owner/name",
    "file_path": "relative/path/to/file.ts",
    "content": "<complete new file content — NOT a partial diff, the FULL file>",
    "description": "One sentence: what changed and why"
  }
]
%%END_REPO_EDIT%%

Rules (MUST follow):
1. Every file object MUST include "repo" with the exact owner/name of the repository.
2. "file_path" is relative within that repository (as in the tree).
3. Include the COMPLETE file content for every file you modify — never a partial diff.
4. You may include files from different repositories in the same array.
5. Place this block at the very end of your message, after your explanation.
6. If you are NOT changing any files, omit this block entirely.${safetyRule}${clarifyFirstRule}
`;
  }

  return `
=== CODE EDIT INSTRUCTIONS ===
If your response INCLUDES ANY CODE CHANGES to files in this repository, you MUST append the following structured block at the very END of your response (after all explanation).
Do NOT include it if you are only explaining code without changing any files.

%%REPO_EDIT%%
[
  {
    "file_path": "relative/path/to/file.ts",
    "content": "<complete new file content — NOT a partial diff, the FULL file>",
    "description": "One sentence: what changed and why"
  }
]
%%END_REPO_EDIT%%

Rules (MUST follow):
1. Include the COMPLETE file content for every file you modify — never a partial diff.
2. Use the exact relative file paths as they appear in the repository tree.
3. You may include multiple files in the array.
4. Place this block at the very end of your message, after your explanation.
5. If you are NOT changing any files, omit this block entirely.${safetyRule}${clarifyFirstRule}
`;
}

export async function buildGitHubChatSystemExtension(opts: {
  selectedGitHubRepo?: { fullName: string; owner: string; name: string };
  selectedGitHubRepos?: Array<{ fullName: string; owner: string; name: string }>;
  cachedRepoContext?: unknown;
  cachedRepoContexts?: Record<string, unknown>;
  lastUserMessageText: string;
}): Promise<GitHubChatSystemExtensionResult> {
  const repos =
    opts.selectedGitHubRepos?.length
      ? opts.selectedGitHubRepos
      : opts.selectedGitHubRepo
        ? [opts.selectedGitHubRepo]
        : [];

  if (repos.length === 0) {
    return {
      prompt: "",
      allowRepoEdit: false,
      contextMode: "rag_only",
      resolvedPaths: [],
      fullFilePaths: [],
      repoTreePaths: [],
    };
  }

  if (repos.length > 1) {
    return buildMultiGitHubChatSystemExtension({
      repos,
      cachedRepoContexts: opts.cachedRepoContexts ?? {},
      lastUserMessageText: opts.lastUserMessageText,
    });
  }

  const selectedGitHubRepo = repos[0];
  const cachedRepoContext =
    opts.cachedRepoContext ??
    opts.cachedRepoContexts?.[selectedGitHubRepo.fullName] ??
    null;
  const repo = cachedRepoContext as {
    info?: { full_name?: string; language?: string; description?: string; default_branch?: string };
    readme?: string;
    tree?: { tree?: Array<{ path: string; type: string }> };
  } | null;

  let repoTreePaths = await resolveRepoTreePaths(
    selectedGitHubRepo.owner,
    selectedGitHubRepo.name,
    cachedRepoContext
  );

  const message = opts.lastUserMessageText?.trim() || selectedGitHubRepo.fullName;
  let contextResult;
  try {
    contextResult = await buildRepoContextWithAgenticRetrieval(
      {
        message,
        owner: selectedGitHubRepo.owner,
        repo: selectedGitHubRepo.name,
        repoTreePaths,
        ref: repo?.info?.default_branch,
      },
      { maxRounds: 2 }
    );
  } catch (e) {
    console.warn("[Chat] Repo context router failed, using minimal context:", e);
    contextResult = {
      decision: {
        mode: "rag_only" as const,
        isEditIntent: false,
        hasFileReference: false,
        hasSymbolReference: false,
        reason: "Router failed",
      },
      ragResults: [],
      candidates: [],
      resolvedPaths: [],
      graphExpandedPaths: [],
      fullFiles: [],
      allowRepoEdit: false,
      retrievalRounds: 0,
      sufficiencyReason: "Router failed",
      taskKind: "explain" as const,
    };
  }

  const {
    decision,
    ragResults: repoChunks,
    fullFiles,
    candidates,
    resolvedPaths,
    graphExpandedPaths,
    retrievalRounds,
    sufficiencyReason,
  } = contextResult;

  const editPlan = decision.isEditIntent
    ? buildEditPlan({ message, contextResult, graphNeighbors: graphExpandedPaths })
    : null;
  const fullFilePathList = fullFiles.map(f => f.path);
  let allowRepoEdit =
    contextResult.allowRepoEdit &&
    (!editPlan || isEditPlanAllowed(editPlan, fullFilePathList));
  const fullFilePaths = new Set(fullFiles.map(f => f.path));

  const parts = [
    `GITHUB REPOSITORY CONTEXT: ${selectedGitHubRepo.fullName}.`,
    repo
      ? `Repository Info: ${repo.info?.full_name}, Language: ${repo.info?.language || "Unknown"}, Description: ${repo.info?.description || "None"}.`
      : "",
    `Context mode: ${decision.mode} (${decision.reason})`,
    retrievalRounds != null && retrievalRounds > 1
      ? `Retrieval: ${retrievalRounds} rounds (${sufficiencyReason ?? "ok"})`
      : "",
  ];

  if (resolvedPaths.length > 0) {
    const sources = candidates
      .filter(c => resolvedPaths.includes(c.path))
      .map(c => `${c.path} [${c.source}, ${c.confidence.toFixed(2)}]`)
      .join("; ");
    if (sources) parts.push(`Resolved paths: ${sources}`);
  }

  if (repo?.readme) {
    const readmePreview = repo.readme.slice(0, 2000);
    parts.push(`\nREADME (preview):\n${readmePreview}${repo.readme.length > 2000 ? "\n..." : ""}`);
  }

  if (fullFiles.length > 0) {
    parts.push("\n=== FULL FILE CONTEXT ===");
    parts.push(
      "The following files were loaded from the repo index or GitHub because the user mentioned a specific file, symbol, or edit request."
    );
    for (const file of fullFiles) {
      const lang = fileExtensionForPath(file.path);
      parts.push(`\n### ${file.path}${file.truncated ? " (truncated to 50KB)" : ""}${file.fromIndex ? " (from repo index)" : ""}\n`);
      parts.push("```" + lang + "\n" + file.content + "\n```");
    }
  }

  if (graphExpandedPaths.length > 0) {
    parts.push("\n=== RELATED FILES (dependency graph) ===");
    parts.push("These files are related via imports, callers, or co-located tests.");
    const graphPathsInContext = graphExpandedPaths.filter(p => fullFilePaths.has(p));
    if (graphPathsInContext.length > 0) {
      parts.push(`Related paths included in full context: ${graphPathsInContext.join(", ")}`);
    } else {
      parts.push(`Graph neighbors: ${graphExpandedPaths.join(", ")}`);
    }
  }

  if (editPlan && decision.isEditIntent) {
    parts.push("\n" + formatEditPlanBlock(editPlan));
  }

  if (repoChunks.length > 0) {
    const skeletonChunks = repoChunks.filter((c) => {
      const t = getChunkType(c);
      return t === "tree" || t === "signature";
    });
    const bodyChunks = repoChunks.filter((c) => {
      const t = getChunkType(c);
      return (
        t === "chunk" &&
        !c.chunk_text.includes("[SIGNATURES]") &&
        !fullFilePaths.has(c.file_path)
      );
    });

    if (skeletonChunks.length > 0) {
      parts.push("\n=== RELEVANT FILES & FUNCTION SIGNATURES ===");
      for (const c of skeletonChunks) {
        parts.push(`\n${c.chunk_text}\n`);
      }
    }

    if (bodyChunks.length > 0) {
      parts.push("\n=== RELEVANT CODE EXCERPTS ===");
      for (const c of bodyChunks) {
        parts.push(`\n${c.chunk_text}\n`);
      }
    }

    parts.push(
      "\nAnswer using the code above. Cite the file path when referencing specific code. If the answer is not in the retrieved excerpts, say so clearly."
    );
  } else if (repo && fullFiles.length === 0) {
    parts.push(
      "\n(Repository is being indexed; only the summary is available for this message. More precise code search will be available shortly.)"
    );
  }

  parts.push(
    buildEditInstructionsBlock({
      allowRepoEdit,
      contextMode: decision.mode,
      hasFullFiles: fullFiles.length > 0,
    })
  );

  return {
    prompt: parts.join("\n"),
    allowRepoEdit,
    contextMode: decision.mode,
    resolvedPaths,
    fullFilePaths: fullFilePathList,
    repoTreePaths,
    retrievalRounds,
    sufficiencyReason,
  };
}

async function buildMultiGitHubChatSystemExtension(opts: {
  repos: Array<{ fullName: string; owner: string; name: string }>;
  cachedRepoContexts: Record<string, unknown>;
  lastUserMessageText: string;
}): Promise<GitHubChatSystemExtensionResult> {
  const message = opts.lastUserMessageText?.trim() || opts.repos.map((r) => r.fullName).join(", ");
  const sectionParts: string[] = [
    `GITHUB REPOSITORIES (${opts.repos.length} attached): ${opts.repos.map((r) => r.fullName).join(", ")}.`,
    "You may propose code edits in any attached repository. Each file in %%REPO_EDIT%% must include a \"repo\" field.",
  ];
  const allRepoTreePaths: string[] = [];
  const allResolvedPaths: string[] = [];
  const allFullFilePaths: string[] = [];
  let allowRepoEdit = false;
  let aggregateContextMode: RepoContextMode = "rag_only";
  let hasAnyFullFiles = false;

  for (const repoRef of opts.repos) {
    const ctx = opts.cachedRepoContexts[repoRef.fullName];
    const repo = ctx as {
      info?: { full_name?: string; language?: string; description?: string; default_branch?: string };
      readme?: string;
      tree?: { tree?: Array<{ path: string; type: string }> };
    } | null;

    let repoTreePaths = await resolveRepoTreePaths(repoRef.owner, repoRef.name, ctx);
    allRepoTreePaths.push(...repoTreePaths.map((p) => `${repoRef.fullName}/${p}`));

    try {
      const contextResult = await buildRepoContextWithAgenticRetrieval(
        {
          message,
          owner: repoRef.owner,
          repo: repoRef.name,
          repoTreePaths,
          ref: repo?.info?.default_branch,
        },
        { maxRounds: 2 }
      );

      const { decision, ragResults: repoChunks, fullFiles, candidates, resolvedPaths, graphExpandedPaths } =
        contextResult;

      if (decision.mode === "full_file_required" || decision.mode === "clarification_required") {
        aggregateContextMode = decision.mode;
      }

      const editPlan = decision.isEditIntent
        ? buildEditPlan({ message, contextResult, graphNeighbors: graphExpandedPaths })
        : null;
      const fullFilePathList = fullFiles.map((f) => f.path);
      const repoAllowEdit =
        contextResult.allowRepoEdit &&
        (!editPlan || isEditPlanAllowed(editPlan, fullFilePathList));
      if (repoAllowEdit) allowRepoEdit = true;
      if (fullFiles.length > 0) hasAnyFullFiles = true;

      sectionParts.push(`\n--- REPO: ${repoRef.fullName} (${decision.mode}) ---`);
      if (repo) {
        sectionParts.push(
          `Repository Info: ${repo.info?.full_name ?? repoRef.fullName}, Language: ${repo.info?.language || "Unknown"}, Description: ${repo.info?.description || "None"}.`
        );
      }

      if (resolvedPaths.length > 0) {
        const sources = candidates
          .filter((c) => resolvedPaths.includes(c.path))
          .map((c) => `${c.path} [${c.source}, ${c.confidence.toFixed(2)}]`)
          .join("; ");
        if (sources) sectionParts.push(`Resolved paths: ${sources}`);
      }

      if (repo?.readme) {
        const readmePreview = repo.readme.slice(0, 1500);
        sectionParts.push(
          `\nREADME (preview):\n${readmePreview}${repo.readme.length > 1500 ? "\n..." : ""}`
        );
      }

      if (fullFiles.length > 0) {
        sectionParts.push("\n=== FULL FILE CONTEXT ===");
        for (const file of fullFiles) {
          const lang = fileExtensionForPath(file.path);
          sectionParts.push(
            `\n### ${repoRef.fullName}/${file.path}${file.truncated ? " (truncated to 50KB)" : ""}${file.fromIndex ? " (from repo index)" : ""}\n\`\`\`${lang}\n${file.content}\n\`\`\``
          );
          allFullFilePaths.push(`${repoRef.fullName}/${file.path}`);
        }
      }

      if (graphExpandedPaths.length > 0) {
        sectionParts.push(`Related files (graph): ${graphExpandedPaths.join(", ")}`);
      }

      if (editPlan && decision.isEditIntent) {
        sectionParts.push(formatEditPlanBlock(editPlan));
      }

      const fullFilePathsSet = new Set(fullFiles.map((f) => f.path));
      const skeletonChunks = repoChunks.filter((c) => {
        const t = getChunkType(c);
        return t === "tree" || t === "signature";
      });
      const bodyChunks = repoChunks.filter((c) => {
        const t = getChunkType(c);
        return (
          t === "chunk" &&
          !c.chunk_text.includes("[SIGNATURES]") &&
          !fullFilePathsSet.has(c.file_path)
        );
      });

      if (skeletonChunks.length > 0) {
        sectionParts.push("Signatures / tree:");
        for (const c of skeletonChunks.slice(0, 8)) {
          sectionParts.push(`[${repoRef.fullName}] ${c.chunk_text.slice(0, 1500)}`);
        }
      }
      if (bodyChunks.length > 0) {
        sectionParts.push("Code excerpts:");
        for (const c of bodyChunks.slice(0, 10)) {
          sectionParts.push(`[${repoRef.fullName}] ${c.chunk_text.slice(0, 2000)}`);
        }
      }

      allResolvedPaths.push(...resolvedPaths.map((p) => `${repoRef.fullName}/${p}`));
    } catch (e) {
      console.warn(`[Chat] Multi-repo context failed for ${repoRef.fullName}:`, e);
      sectionParts.push(`(${repoRef.fullName}: retrieval failed)`);
    }
  }

  sectionParts.push("\nWhen citing code, always include the repository name and file path.");

  sectionParts.push(
    buildEditInstructionsBlock({
      allowRepoEdit,
      contextMode: aggregateContextMode,
      hasFullFiles: hasAnyFullFiles,
      multiRepo: true,
    })
  );

  return {
    prompt: sectionParts.join("\n"),
    allowRepoEdit,
    contextMode: aggregateContextMode,
    resolvedPaths: allResolvedPaths,
    fullFilePaths: allFullFilePaths,
    repoTreePaths: allRepoTreePaths,
  };
}

export function buildThinkingChatSystemExtension(opts: {
  effectiveThinkingMode: boolean;
  supportsThinking: boolean;
}): string {
  if (opts.effectiveThinkingMode && opts.supportsThinking) {
    return `THINKING MODE ENABLED: Please engage in extended reasoning. Before your final answer, think step-by-step. Show your thought process when helpful.`;
  }
  return "";
}

const WORKFLOW_FORK_THREAD_PREFIX = "workflow-fork-";

/** Inject workflow checkpoint context when user is refining a paused workflow node in chat. */
export async function buildWorkflowForkChatSystemExtension(
  threadId: string | null
): Promise<string | null> {
  if (typeof threadId !== "string" || !threadId.startsWith(WORKFLOW_FORK_THREAD_PREFIX)) return null;

  const { workflowRunsService } = await import("@/services/workflowRunsService");
  const checkpoint = await workflowRunsService.getCheckpointByChatThreadId(threadId);
  if (!checkpoint) return null;

  const run = await workflowRunsService.getRun(checkpoint.runId);
  const output =
    checkpoint.outputOverride ??
    run?.state?.nodeOutputs?.[checkpoint.nodeId]?.parsed ??
    run?.state?.nodeOutputs?.[checkpoint.nodeId]?.raw;

  const contextBlocks = run?.state?.contextBlocks ?? {};
  const contextSection =
    Object.keys(contextBlocks).length > 0
      ? `\n\nLoaded workflow context:\n${Object.values(contextBlocks)
          .map((b) => `- ${b.title}: ${String(b.content).slice(0, 2000)}`)
          .join("\n")}`
      : "";

  return `WORKFLOW FORK SESSION
You are helping refine output for workflow run ${checkpoint.runId} at node "${checkpoint.nodeId}".
The user may edit, expand, or correct this output. When they confirm the result is ready, they will merge it back into the workflow.

Current node output:
${typeof output === "string" ? output : JSON.stringify(output, null, 2)}${contextSection}

Respond concisely. Focus on improving the workflow node output based on user feedback.`;
}
